import { debounce } from 'lodash';
import {
  useState,
  useCallback,
  useContext,
  useRef,
  useEffect,
  useMemo,
} from 'react';

import {
  AnalyticsModifiersContext,
  UserSessionValuesContext,
  BookValuesContext,
} from 'contexts';
import { ampli } from 'ampli';
import { getLocalSettingsProxy } from 'utilities';
import {
  CookieCategory,
  getCookieConsentStorageKey,
} from 'modules/CookieConsent/hooks/useCookieConsent';

export const AnalyticsProvider = ({ children }) => {
  const { analyticsMetadata, user } = useContext(UserSessionValuesContext);
  const isTrackingDisabled = useRef(false);

  const resolveStaticMetadataPromise = useRef();
  const [staticMetadataPromise] = useState(
    new Promise((resolve) => {
      resolveStaticMetadataPromise.current = resolve;
    }),
  );

  const { bookPk } = useContext(BookValuesContext);

  useEffect(
    function loadAmplitude() {
      if (ampli.isLoaded) {
        return;
      }

      try {
        const storage = getLocalSettingsProxy();
        const cookieConsentRaw = storage[getCookieConsentStorageKey(user?.id)];

        if (cookieConsentRaw) {
          const cookieConsent = JSON.parse(cookieConsentRaw);
          if (cookieConsent[CookieCategory.PERFORMANCE] === false) {
            isTrackingDisabled.current = true;
            console.log('Analytics tracking disabled due to cookie consent');
            return;
          }
        }
      } catch (error) {
        console.error('Error parsing cookie consent:', error);
      }

      /* see RP-5572 to come up with a good system for the ENVs */
      console.log('analytics env', process.env);
      ampli.load({
        environment: 'production',
        client: {
          configuration: {
            pageViewTracking: false, // reader determines pageviews
          },
        },
      });
    },
    [user?.id],
  );

  // bootstrap the user and set our user props
  useEffect(
    function setAmplitudeUser() {
      if (isTrackingDisabled.current) {
        return;
      }

      // We want this to run once the client is loaded, and when we don't
      // yet had an amplitude user
      if (!ampli.client || ampli.client?.getUserId() === user.uuid) {
        return;
      }
      ampli.identify(user.uuid, {
        ...analyticsMetadata,
      });
    },
    [user.uuid, analyticsMetadata],
  );

  /* This will likely be iterated on
   * we're using a custom session id in amplitude
   */
  useEffect(() => {
    if (!analyticsMetadata) {
      return; // wait for analyticsMetadata to be available
    }

    let amplitudeSessionId = ampli?.amplitude?.getSessionId();
    if (!amplitudeSessionId) {
      amplitudeSessionId = new Date().getTime();
    }

    const bookIdSession = String(bookPk) + amplitudeSessionId;
    const sessionIdVersion1 = { bookIdSession };

    resolveStaticMetadataPromise.current({
      ...analyticsMetadata,
      ...sessionIdVersion1,
    });
  }, [analyticsMetadata, bookPk]);

  const bookmarkCreated = useCallback(async () => {
    if (isTrackingDisabled.current) return;

    const staticMetadata = await staticMetadataPromise;

    ampli.bookmarkCreated({ ...staticMetadata });
  }, [staticMetadataPromise]);

  const citationCreated = useCallback(async () => {
    if (isTrackingDisabled.current) return;

    const staticMetadata = await staticMetadataPromise;

    ampli.citationCreated({ ...staticMetadata });
  }, [staticMetadataPromise]);

  const collaborationMenuOpen = useCallback(async () => {
    if (isTrackingDisabled.current) return;

    const staticMetadata = await staticMetadataPromise;

    ampli.collaborationMenuOpen({ ...staticMetadata });
  }, [staticMetadataPromise]);

  const collaborationNoteSubscribed = useCallback(async () => {
    if (isTrackingDisabled.current) return;

    const staticMetadata = await staticMetadataPromise;

    ampli.collaborationNoteSubscribed({ ...staticMetadata });
  }, [staticMetadataPromise]);

  const downloadToOfflineMenuOpen = useCallback(async () => {
    if (isTrackingDisabled.current) return;

    const staticMetadata = await staticMetadataPromise;

    ampli.downloadToOfflineMenuOpen({ ...staticMetadata });
  }, [staticMetadataPromise]);

  const flashcardCreated = useCallback(async () => {
    if (isTrackingDisabled.current) return;

    const staticMetadata = await staticMetadataPromise;

    ampli.flashcardCreated({ ...staticMetadata });
  }, [staticMetadataPromise]);

  const highlightCreated = useCallback(
    async (eventProperties = {}) => {
      if (isTrackingDisabled.current) return;

      const staticMetadata = await staticMetadataPromise;

      ampli.highlightCreated({ ...eventProperties, ...staticMetadata });
    },
    [staticMetadataPromise],
  );

  const myShelfClicked = useCallback(async () => {
    if (isTrackingDisabled.current) return;

    const staticMetadata = await staticMetadataPromise;

    ampli.myShelfClicked({ ...staticMetadata });
  }, [staticMetadataPromise]);

  const noteCreated = useCallback(
    async (eventProperties = {}) => {
      if (isTrackingDisabled.current) return;

      const staticMetadata = await staticMetadataPromise;

      ampli.noteCreated({ ...eventProperties, ...staticMetadata });
    },
    [staticMetadataPromise],
  );

  const offlineChapterDeleted = useCallback(
    async (eventProperties = {}) => {
      if (isTrackingDisabled.current) return;

      const staticMetadata = await staticMetadataPromise;

      ampli.offlineDelete({ ...eventProperties, ...staticMetadata });
    },
    [staticMetadataPromise],
  );

  const offlineChapterDownloaded = useCallback(
    async (eventProperties = {}) => {
      if (isTrackingDisabled.current) return;

      const staticMetadata = await staticMetadataPromise;

      ampli.offlineDownload({ ...eventProperties, ...staticMetadata });
    },
    [staticMetadataPromise],
  );

  const pageViewed = useCallback(
    debounce(async (eventProperties) => {
      if (isTrackingDisabled.current) return;

      const staticMetadata = await staticMetadataPromise;

      ampli.pageViewed({
        ...eventProperties,
        ...staticMetadata,
        bookId: bookPk,
      });
    }, 1000),
    [staticMetadataPromise, bookPk],
  );

  const printToPdfMenuOpen = useCallback(async () => {
    if (isTrackingDisabled.current) return;

    const staticMetadata = await staticMetadataPromise;

    ampli.printToPdfMenuOpen({ ...staticMetadata });
  }, [staticMetadataPromise]);

  const searchMenuOpen = useCallback(async () => {
    if (isTrackingDisabled.current) return;

    const staticMetadata = await staticMetadataPromise;

    ampli.searchMenuOpen({ ...staticMetadata });
  }, [staticMetadataPromise]);

  const submitATicketClicked = useCallback(async () => {
    if (isTrackingDisabled.current) return;

    const staticMetadata = await staticMetadataPromise;

    ampli.submitATicketClicked({ ...staticMetadata });
  }, [staticMetadataPromise]);

  const tableOfContentsMenuOpen = useCallback(async () => {
    if (isTrackingDisabled.current) return;

    const staticMetadata = await staticMetadataPromise;

    ampli.tableOfContentsMenuOpen({ ...staticMetadata });
  }, [staticMetadataPromise]);

  const TTSStarted = useCallback(
    async (eventProperties) => {
      if (isTrackingDisabled.current) return;

      const staticMetadata = await staticMetadataPromise;

      ampli.ttsStarted({
        ...eventProperties,
        ...staticMetadata,
        bookId: bookPk,
      });
    },
    [staticMetadataPromise, bookPk],
  );

  const modifiers = useMemo(
    () => ({
      bookmarkCreated,
      citationCreated,
      collaborationMenuOpen,
      collaborationNoteSubscribed,
      downloadToOfflineMenuOpen,
      flashcardCreated,
      highlightCreated,
      myShelfClicked,
      noteCreated,
      offlineChapterDeleted,
      offlineChapterDownloaded,
      pageViewed,
      printToPdfMenuOpen,
      searchMenuOpen,
      submitATicketClicked,
      tableOfContentsMenuOpen,
      TTSStarted,
    }),
    [
      bookmarkCreated,
      citationCreated,
      collaborationMenuOpen,
      collaborationNoteSubscribed,
      downloadToOfflineMenuOpen,
      flashcardCreated,
      highlightCreated,
      myShelfClicked,
      noteCreated,
      offlineChapterDeleted,
      offlineChapterDownloaded,
      pageViewed,
      printToPdfMenuOpen,
      searchMenuOpen,
      submitATicketClicked,
      tableOfContentsMenuOpen,
      TTSStarted,
    ],
  );

  return (
    <AnalyticsModifiersContext.Provider value={modifiers}>
      {children}
    </AnalyticsModifiersContext.Provider>
  );
};

export default AnalyticsProvider;
